import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_HOME_LAYOUT,
  type HomeLayout,
  type HomeWidget,
  HOME_WIDGETS,
  isDefaultHomeLayout,
  moveWidget,
  placeWidget,
  toggleWidget,
  useHomeLayout,
} from '@/entities/viewer';
import { FormError } from '@/shared/forms';
import { Button, IconButton, Icons } from '@/shared/ui';

import styles from './HomeCustomizePanel.module.css';
import { type RowEdge, useTouchLift } from './use-touch-lift';

/** A finger-driven drag in progress: the lifted row and where it would land. */
interface TouchDrag {
  source: HomeWidget;
  over: HomeWidget | null;
  edge: RowEdge;
}

const DRAG_TYPE = 'home-widget';
const isWidgetDrag = (data: Record<string | symbol, unknown>): data is { widget: HomeWidget } =>
  data['type'] === DRAG_TYPE && (HOME_WIDGETS as readonly unknown[]).includes(data['widget']);

interface RowProps {
  id: HomeWidget;
  index: number;
  count: number;
  hidden: boolean;
  grabbed: boolean;
  /** This row is lifted by a finger / the drop line a finger shows on it. */
  lifted: boolean;
  fingerEdge: RowEdge | null;
  onLift: () => void;
  onOver: (target: HomeWidget | null, edge: RowEdge) => void;
  onRelease: (target: HomeWidget | null, edge: RowEdge) => void;
  onToggle: () => void;
  onStep: (step: -1 | 1) => void;
  onGrab: () => void;
  onDrop: (sourceId: HomeWidget, edge: Edge | null) => void;
}

/**
 * One section of the customization list: the handle drags it (mouse / pen: the native drag of
 * pragmatic-dnd; a finger: press and hold, see `useTouchLift`); from the keyboard ArrowUp /
 * ArrowDown on the focused handle move it. A plain tap on the handle "picks the section up": only
 * then do two small step buttons appear on THAT row (for assistive technology without dragging).
 */
function Row({
  id,
  index,
  count,
  hidden,
  grabbed,
  lifted,
  fingerEdge,
  onLift,
  onOver,
  onRelease,
  onToggle,
  onStep,
  onGrab,
  onDrop,
}: RowProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLLIElement>(null);
  const handle = useRef<HTMLButtonElement>(null);
  const [dragging, setDragging] = useState(false);
  const [edge, setEdge] = useState<Edge | null>(null);
  const name = t(`home.customize.widgets.${id}`);
  const dropped = useEffectEvent(onDrop);
  const touch = useTouchLift(handle, {
    onLift,
    onOver: (target, edge) => {
      onOver(target as HomeWidget | null, edge);
    },
    onRelease: (target, edge) => {
      onRelease(target as HomeWidget | null, edge);
    },
  });

  useEffect(() => {
    const element = ref.current;
    const grip = handle.current;
    if (!element || !grip) return;
    return combine(
      draggable({
        element,
        dragHandle: grip,
        // a finger has its own press-and-hold gesture (the native one cannot be told apart)
        canDrag: () => !window.matchMedia('(pointer: coarse)').matches,
        getInitialData: () => ({ type: DRAG_TYPE, widget: id }),
        onDragStart: () => {
          setDragging(true);
        },
        onDrop: () => {
          setDragging(false);
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => isWidgetDrag(source.data) && source.data.widget !== id,
        getData: ({ input }) =>
          attachClosestEdge(
            { type: DRAG_TYPE, widget: id },
            { element, input, allowedEdges: ['top', 'bottom'] },
          ),
        onDrag: ({ self }) => {
          setEdge(extractClosestEdge(self.data));
        },
        onDragLeave: () => {
          setEdge(null);
        },
        onDrop: ({ source, self }) => {
          setEdge(null);
          if (isWidgetDrag(source.data)) dropped(source.data.widget, extractClosestEdge(self.data));
        },
      }),
    );
  }, [id]);

  return (
    <li
      ref={ref}
      className={[
        styles.row,
        dragging && styles.dragging,
        lifted && styles.lifted,
        (edge ?? fingerEdge) && styles[`drop-${edge ?? fingerEdge ?? 'top'}`],
      ]
        .filter(Boolean)
        .join(' ')}
      data-hidden={hidden || undefined}
      data-grabbed={grabbed || undefined}
      data-widget-row={id}
    >
      <button
        ref={handle}
        type="button"
        className={styles.handle}
        data-handle={id}
        aria-pressed={grabbed}
        aria-label={t('home.customize.handle', { name, position: index + 1, total: count })}
        title={t('home.customize.handleHint')}
        onClick={() => {
          if (!touch.consumedClick()) onGrab();
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp' && index > 0) {
            event.preventDefault();
            onStep(-1);
          } else if (event.key === 'ArrowDown' && index < count - 1) {
            event.preventDefault();
            onStep(1);
          }
        }}
      >
        <Icons.grip />
      </button>
      <label className={styles.show}>
        <input type="checkbox" checked={!hidden} onChange={onToggle} />
        <span>{name}</span>
      </label>
      {grabbed && (
        <span className={styles.moves}>
          <IconButton
            label={t('home.customize.moveUp', { name })}
            icon={<Icons.arrowUp />}
            disabled={index === 0}
            onClick={() => {
              onStep(-1);
            }}
          />
          <IconButton
            label={t('home.customize.moveDown', { name })}
            icon={<Icons.arrowDown />}
            disabled={index === count - 1}
            onClick={() => {
              onStep(1);
            }}
          />
        </span>
      )}
    </li>
  );
}

/**
 * Customization mode of the signed-in Home: show or hide each section and drag it into place (a
 * visible handle per row; the keyboard and a touch screen have their own ways, see `Row`). Each
 * change is saved to the account at once; "Restore default" goes back to the standard Home.
 * Account navigation, the global search and the Create menu are not sections and never hide.
 */
export function HomeCustomizePanel({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const home = useHomeLayout();
  // The panel edits its own copy so every control answers at once; each change is also saved.
  const [layout, setLayout] = useState<HomeLayout>(home.layout);
  const [grabbed, setGrabbed] = useState<HomeWidget | null>(null);
  const [finger, setFinger] = useState<TouchDrag | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const refocus = useRef<HomeWidget | null>(null);
  const commit = (next: HomeLayout, moved?: HomeWidget) => {
    if (next === layout) return;
    setLayout(next);
    home.change(next);
    if (moved) {
      const position = next.order.indexOf(moved) + 1;
      setAnnouncement(
        t('home.customize.moved', {
          name: t(`home.customize.widgets.${moved}`),
          position,
          total: next.order.length,
        }),
      );
    }
  };

  // Reordering re-inserts the row; a focused handle must keep the focus (keyboard stepping).
  useEffect(() => {
    if (!refocus.current) return;
    document.querySelector<HTMLElement>(`[data-handle="${refocus.current}"]`)?.focus();
    refocus.current = null;
  }, [layout.order]);

  return (
    <section
      className={styles.panel}
      aria-label={t('home.customize.title')}
      data-testid="home-customize"
    >
      <header className={styles.head}>
        <h2 className={styles.title}>{t('home.customize.title')}</h2>
        <p className={styles.hint}>{t('home.customize.hint')}</p>
      </header>
      <ol className={styles.list}>
        {layout.order.map((id: HomeWidget, index) => (
          <Row
            key={id}
            id={id}
            index={index}
            count={layout.order.length}
            hidden={layout.hidden.includes(id)}
            grabbed={grabbed === id}
            lifted={finger?.source === id}
            fingerEdge={finger?.over === id && finger.source !== id ? finger.edge : null}
            onLift={() => {
              setGrabbed(null);
              setFinger({ source: id, over: null, edge: 'top' });
            }}
            onOver={(over, edge) => {
              setFinger((current) => (current ? { ...current, over, edge } : current));
            }}
            onRelease={(over, edge) => {
              setFinger(null);
              if (over && over !== id) {
                commit(placeWidget(layout, id, over, edge === 'top' ? 'before' : 'after'), id);
              }
            }}
            onToggle={() => {
              commit(toggleWidget(layout, id));
            }}
            onGrab={() => {
              setGrabbed((current) => (current === id ? null : id));
            }}
            onStep={(step) => {
              if (document.activeElement?.getAttribute('data-handle') === id) refocus.current = id;
              commit(moveWidget(layout, id, step), id);
            }}
            onDrop={(sourceId, edge) => {
              commit(
                placeWidget(layout, sourceId, id, edge === 'top' ? 'before' : 'after'),
                sourceId,
              );
            }}
          />
        ))}
      </ol>
      <p className={styles.live} role="status" aria-live="polite">
        {announcement}
      </p>
      <FormError error={home.error} />
      <div className={styles.actions}>
        <Button
          disabled={isDefaultHomeLayout(layout)}
          onClick={() => {
            setLayout({ order: [...DEFAULT_HOME_LAYOUT.order], hidden: [] });
            setGrabbed(null);
            home.reset();
          }}
        >
          {t('home.customize.reset')}
        </Button>
        <Button variant="primary" onClick={onDone}>
          {t('home.customize.done')}
        </Button>
      </div>
    </section>
  );
}
